import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  ComposedModal,
  InlineLoading,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PasswordInput,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  TextInput,
} from '@carbon/react';
import { Add } from '@carbon/icons-react';
import {
  ActiveSession,
  api,
  Facility,
  ROLE_BLURBS,
  ROLE_LABELS,
  User,
  UserRole,
} from '../lib/api';
import { formatDateTime, relativeTime } from '../lib/format';
import { useClientPagination } from '../lib/pagination';
import { Card, TablePagination, useToast } from '../components/ui';

export function Users({
  currentUser,
  assignableRoles,
}: {
  currentUser: User;
  /** Roles this account may grant. Nobody may create an account above their own level. */
  assignableRoles: UserRole[];
}) {
  const [rows, setRows] = useState<User[]>([]);
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<User | 'new' | null>(null);
  const pagination = useClientPagination(rows);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [users, active, facs] = await Promise.all([
        api.get<User[]>('/api/users'),
        api.get<ActiveSession[]>('/api/sessions'),
        api.get<Facility[]>('/api/facilities'),
      ]);
      setRows(users);
      setSessions(active);
      setFacilities(facs);
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  async function removeUser(user: User) {
    if (!confirm(`Remove ${user.name}?`)) return;
    try {
      await api.del(`/api/users/${user.id}`);
      toast('ok', `${user.name} removed`);
      await load();
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    }
  }

  return (
    <>
      <div className="users-toolbar">
        <span className="users-toolbar-helper">
          Every account gets an identifier when it is created; an account without one cannot sign in.
          Roles take effect on the user's next request, not when their session expires.
        </span>
        <Button
          kind="primary"
          size="md"
          renderIcon={Add}
          onClick={() => setEditing('new')}
        >
          Add user
        </Button>
      </div>

      <Card bodyless>
        <div className="users-results-panel">
          {loading && rows.length === 0 ? (
            <div className="users-results-empty">
              <InlineLoading description="Loading users…" />
            </div>
          ) : rows.length === 0 ? (
            <div className="users-results-empty">
              <div className="users-empty-title">No users found</div>
              <div className="users-empty-desc">
                No user accounts are registered in the hub.
              </div>
            </div>
          ) : (
            <div className="table-wrap">
              <Table size="sm">
                <TableHead>
                  <TableRow>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Identifier</TableHeader>
                    <TableHeader>Email</TableHeader>
                    <TableHeader>Role</TableHeader>
                    <TableHeader>Data access</TableHeader>
                    <TableHeader>Last sign-in</TableHeader>
                    <TableHeader />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagination.pageItems.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="row" style={{ alignItems: 'center', gap: 6 }}>
                          <span style={{ fontWeight: 560 }}>{user.name}</span>
                          {user.id === currentUser.id ? (
                            <Tag type="blue" size="sm" className="user-status-tag">
                              You
                            </Tag>
                          ) : null}
                          {user.disabled ? (
                            <Tag type="gray" size="sm" className="user-status-tag">
                              Disabled
                            </Tag>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="mono">
                        {user.identifier ?? (
                          <Tag type="red" size="sm" className="user-status-tag">
                            Missing
                          </Tag>
                        )}
                      </TableCell>
                      <TableCell className="muted">{user.email}</TableCell>
                      <TableCell>
                        <Tag
                          type={user.role === 'superadmin' ? 'purple' : 'cool-gray'}
                          size="sm"
                          className="user-status-tag"
                        >
                          {ROLE_LABELS[user.role] ?? user.role}
                        </Tag>
                      </TableCell>
                      <TableCell>
                        {user.role === 'superadmin' || user.scopeAll ? (
                          <span className="muted">All facilities</span>
                        ) : (user.facilityIds?.length ?? 0) === 0 ? (
                          <Tag type="red" size="sm" className="user-status-tag">
                            No facilities
                          </Tag>
                        ) : (
                          <span className="muted">
                            {user.facilityIds!.length} facilit{user.facilityIds!.length === 1 ? 'y' : 'ies'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="muted" title={formatDateTime(user.lastLoginAt)}>
                        {relativeTime(user.lastLoginAt)}
                      </TableCell>
                      <TableCell>
                        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                          <Button
                            kind="ghost"
                            size="sm"
                            onClick={() => setEditing(user)}
                          >
                            Edit
                          </Button>
                          {user.id !== currentUser.id ? (
                            <Button
                              kind="danger--ghost"
                              size="sm"
                              onClick={() => removeUser(user)}
                            >
                              Delete
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {rows.length > 0 ? (
            <TablePagination
              {...pagination}
              labels={{
                backwardText: 'Previous users page',
                forwardText: 'Next users page',
                itemsPerPageText: 'Users per page:',
                pageSelectLabelText: (total) =>
                  `Users page, of ${total} ${total === 1 ? 'page' : 'pages'}`,
              }}
            />
          ) : null}
        </div>
      </Card>

      <div style={{ marginTop: 24 }}>
        <SessionsCard
          sessions={sessions}
          onRevoked={load}
        />
      </div>

      {editing ? (
        <UserModal
          user={editing === 'new' ? null : editing}
          assignableRoles={assignableRoles}
          facilities={facilities}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      ) : null}
    </>
  );
}

function UserModal({
  user,
  assignableRoles,
  facilities,
  onClose,
  onSaved,
}: {
  user: User | null;
  assignableRoles: UserRole[];
  facilities: Facility[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<UserRole>(user?.role ?? 'viewer');
  const [password, setPassword] = useState('');
  const [disabled, setDisabled] = useState(user?.disabled ?? false);
  const [scopeAll, setScopeAll] = useState(user?.scopeAll ?? false);
  const [facilityIds, setFacilityIds] = useState<number[]>(user?.facilityIds ?? []);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const passwordOk = user ? password === '' || password.length >= 10 : password.length >= 10;
  // A super administrator is unrestricted by definition, so the scope controls do not apply.
  const scoped = role !== 'superadmin' && !scopeAll;
  const scopeOk = !scoped || facilityIds.length > 0;

  function toggleFacility(id: number) {
    setFacilityIds((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );
  }

  async function save() {
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        role,
        scopeAll: role === 'superadmin' ? true : scopeAll,
        facilityIds: role === 'superadmin' || scopeAll ? [] : facilityIds,
        ...(password ? { password } : {}),
      };
      if (user) {
        await api.patch(`/api/users/${user.id}`, { ...payload, disabled });
      } else {
        await api.post('/api/users', { ...payload, email: email.trim(), password });
      }
      toast('ok', `${name.trim()} saved`);
      onSaved();
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ComposedModal open onClose={onClose} size="sm" className="users-modal">
      <ModalHeader title={user ? `Edit ${user.name}` : 'Add user'} closeModal={onClose} />
      <ModalBody>
        <div className="users-modal-form">
          <TextInput
            id="user-name-input"
            labelText="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />

          <TextInput
            id="user-email-input"
            type="email"
            labelText="Email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={Boolean(user)}
            autoComplete="off"
          />

          <Select
            id="user-role-select"
            labelText="Role"
            helperText={ROLE_BLURBS[role]}
            value={role}
            onChange={(event) => setRole(event.target.value as UserRole)}
          >
            {assignableRoles.map((option) => (
              <SelectItem
                key={option}
                value={option}
                text={ROLE_LABELS[option] ?? option}
              />
            ))}
          </Select>

          {role === 'superadmin' ? (
            <InlineNotification
              kind="warning"
              title=""
              subtitle="A super administrator can see every facility and manage users, DHIS2 credentials and deletions. Grant it sparingly."
              hideCloseButton
              className="users-notification"
            />
          ) : (
            <Select
              id="user-scope-select"
              labelText="Data access"
              helperText={
                scopeOk
                  ? 'Which facilities this account may see. Everything else is filtered out server-side.'
                  : undefined
              }
              invalid={!scopeOk}
              invalidText="Select at least one facility, or give access to all of them."
              value={scopeAll ? 'all' : 'selected'}
              onChange={(event) => setScopeAll(event.target.value === 'all')}
            >
              <SelectItem value="all" text="All facilities, including ones added later" />
              <SelectItem value="selected" text="Only the facilities I select" />
            </Select>
          )}

          {scoped ? (
            <div className="users-facility-picker">
              {facilities.length === 0 ? (
                <span className="users-facility-empty">No facilities are registered yet.</span>
              ) : (
                facilities.map((facility) => (
                  <Checkbox
                    key={facility.id}
                    id={`user-facility-${facility.id}`}
                    labelText={
                      <>
                        {facility.name} <span className="mono faint">({facility.code})</span>
                      </>
                    }
                    checked={facilityIds.includes(facility.id)}
                    onChange={() => toggleFacility(facility.id)}
                  />
                ))
              )}
            </div>
          ) : null}

          <PasswordInput
            id="user-password-input"
            labelText={user ? 'New password' : 'Password'}
            helperText={
              passwordOk
                ? user
                  ? 'Leave blank to keep the current password. Changing it signs the user out.'
                  : 'At least 10 characters.'
                : undefined
            }
            invalid={!passwordOk}
            invalidText="At least 10 characters."
            placeholder=""
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
          />

          {user ? (
            <Checkbox
              id="user-disabled-checkbox"
              labelText="Disable this account"
              checked={disabled}
              onChange={(_event, { checked }) => setDisabled(checked)}
            />
          ) : null}

          {user ? (
            <InlineNotification
              kind="info"
              title=""
              subtitle="Changing the role, data access or password signs this user out immediately, so the new limits take effect at once."
              hideCloseButton
              className="users-notification"
            />
          ) : null}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          kind="primary"
          onClick={save}
          disabled={busy || !name.trim() || (!user && !email.trim()) || !passwordOk || !scopeOk}
        >
          {busy ? <InlineLoading status="active" description="" /> : null}
          Save
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}

/**
 * Who is signed in right now.
 *
 * One session per account is enforced at sign-in, so this list has at most one row per person. The
 * pair of addresses matters: a session whose last-seen address differs from where it signed in has
 * either moved network or is being used from somewhere else.
 */
function SessionsCard({
  sessions,
  onRevoked,
}: {
  sessions: ActiveSession[];
  onRevoked: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<number | null>(null);
  const pagination = useClientPagination(sessions);
  const toast = useToast();

  async function revoke(session: ActiveSession) {
    if (!confirm(`Sign ${session.userName} out of ${session.deviceLabel ?? 'their device'}?`)) {
      return;
    }
    setBusy(session.id);
    try {
      await api.del(`/api/sessions/${session.id}`);
      toast('ok', `${session.userName} was signed out`);
      await onRevoked();
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card
      title="Active sessions"
      actions={<span className="users-sessions-helper">One device per account</span>}
      bodyless
    >
      <div className="users-sessions-panel">
        {sessions.length === 0 ? (
          <div className="users-sessions-empty">
            <div className="users-empty-title">Nobody is signed in</div>
            <div className="users-empty-desc">Sessions appear here as people sign in.</div>
          </div>
        ) : (
          <div className="table-wrap">
            <Table size="sm">
              <TableHead>
                <TableRow>
                  <TableHeader>User</TableHeader>
                  <TableHeader>Device</TableHeader>
                  <TableHeader>Signed in from</TableHeader>
                  <TableHeader>Last seen from</TableHeader>
                  <TableHeader>Last activity</TableHeader>
                  <TableHeader />
                </TableRow>
              </TableHead>
              <TableBody>
                {pagination.pageItems.map((session) => {
                  const moved =
                    session.lastIp && session.ipAddress && session.lastIp !== session.ipAddress;
                  return (
                    <TableRow key={session.id}>
                      <TableCell>
                        <div className="stack" style={{ gap: 2 }}>
                          <span style={{ fontWeight: 560 }}>{session.userName}</span>
                          <span className="muted small">{session.userEmail}</span>
                        </div>
                      </TableCell>
                      <TableCell className="muted">{session.deviceLabel ?? '—'}</TableCell>
                      <TableCell className="mono">{session.ipAddress ?? '—'}</TableCell>
                      <TableCell>
                        <div className="stack" style={{ gap: 4 }}>
                          <span className="mono">{session.lastIp ?? '—'}</span>
                          {moved ? (
                            <div>
                              <Tag type="warm-gray" size="sm" className="user-status-tag">
                                Address changed
                              </Tag>
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="muted" title={formatDateTime(session.lastSeenAt)}>
                        {relativeTime(session.lastSeenAt)}
                      </TableCell>
                      <TableCell>
                        <div className="row" style={{ justifyContent: 'flex-end' }}>
                          <Button
                            kind="danger--ghost"
                            size="sm"
                            disabled={busy === session.id}
                            onClick={() => revoke(session)}
                          >
                            {busy === session.id ? (
                              <InlineLoading status="active" description="" />
                            ) : (
                              'Sign out'
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {sessions.length > 0 ? (
          <TablePagination
            {...pagination}
            labels={{
              backwardText: 'Previous sessions page',
              forwardText: 'Next sessions page',
              itemsPerPageText: 'Sessions per page:',
              pageSelectLabelText: (total) =>
                `Sessions page, of ${total} ${total === 1 ? 'page' : 'pages'}`,
            }}
          />
        ) : null}
      </div>
    </Card>
  );
}
